import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';

const DefaultFallback = () => (
  <div className="fixed inset-0 flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
  </div>
);

/**
 * Gate for the signed in part of the app.
 *
 * Much smaller than the Base44 version, because two of the three states it
 * used to handle no longer exist. It rendered a PendingApproval screen when
 * the separate UserApproval table said a user was not approved yet, and a
 * UserNotRegisteredError screen when Base44 reported the account was not
 * registered for this app. The approval table had zero rows in production and
 * is cut, and there is no per app registration to fail.
 *
 * A suspended account is now refused by the API itself, on every request, so
 * it arrives here simply as not authenticated and goes to the login page.
 */
export default function ProtectedRoute({ fallback = <DefaultFallback />, unauthenticatedElement }) {
  const { isAuthenticated, isLoadingAuth, authChecked, checkUserAuth } = useAuth();

  useEffect(() => {
    if (!authChecked && !isLoadingAuth) {
      checkUserAuth();
    }
  }, [authChecked, isLoadingAuth, checkUserAuth]);

  if (isLoadingAuth || !authChecked) {
    return fallback;
  }

  return isAuthenticated ? <Outlet /> : unauthenticatedElement;
}
